import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import Popup from 'reactjs-popup';
import { ByzzerTextInput } from '@byzzer/ui-components';
import generalStyles from '@/components/General/Styles.module.scss';
import styles from '@/components/BlacklistEmail/BlacklistEmail.module.scss';
import DataTable from '@/components/General/DataTable';
import store from '@/store';
import { getBlacklistedDomains, blacklistDomain } from '@/services/api/company.service';
import API_STATES from '@/constants/StateConstants';
import CSR_ROLES from '@/constants/CsrRoles';

const BlacklistEmail = () => {
    const [openModal, setOpenModal] = useState(false);
    const [tableData, setTableData] = React.useState([]);
    const [inputValue, setInputValue] = useState('');
    const [apiStates, setApiStates] = useState({
        blacklistedDomains: {
            state: API_STATES.none,
            error: '',
        },
        addDomain: {
            state: API_STATES.none,
            successMessage: '',
            error: '',
        },
    });

    const storeState = store?.getState();
    const roles = storeState?.account?.user?.csrUserRoles;

    const isValidDomain = () => {
        const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return domainPattern.test(inputValue);
    };

    const onSubmit = async () => {
        try {
            if (!isValidDomain()) {
                setApiStates({
                    ...apiStates,
                    addDomain: { state: API_STATES.error, error: 'Please enter a valid domain.', successMessage: '' },
                });
                return;
            }
            setApiStates({ ...apiStates, addDomain: { state: API_STATES.loading, error: '' } });

            const requestData = {
                restrictionType: 'domain',
                restrictionValue: inputValue,
                restrictionMetadata: {
                    restrictionDomainType: 'generic',
                },
            };
            const response = await blacklistDomain(requestData);

            if (response.status === API_STATES.success) {
                setApiStates({
                    ...apiStates,
                    addDomain: { state: API_STATES.success, error: '', successMessage: 'Domain added successfully!' },
                });
                setInputValue('');
                setTableData((prevData) => [...prevData, response.data]);

                setTimeout(() => {
                    setApiStates({
                        ...apiStates,
                        addDomain: { state: API_STATES.none, successMessage: '', error: '' },
                    });
                }, 5000);
            } else {
                setApiStates({ ...apiStates, addDomain: { state: API_STATES.error, error: response.error.message } });
            }
        } catch (error) {
            setApiStates({ ...apiStates, addDomain: { state: API_STATES.error, error: 'Something went wrong!' } });
        }
    };

    useEffect(() => {
        (async () => {
            setApiStates({ ...apiStates, blacklistedDomains: { state: API_STATES.loading, error: '' } });
            const response = await getBlacklistedDomains();
            if (response.status === API_STATES.success) {
                const companyData = response.data;
                setTableData(companyData);
                setApiStates({ ...apiStates, companies: { state: API_STATES.success, error: '' } });
            } else {
                setApiStates({ ...apiStates, companies: { state: API_STATES.error, error: response.error.message } });
            }
        })();
    }, []);

    const columnDef = React.useMemo(
        () => [
            {
                Header: 'Domain',
                accessor: 'restrictionValue',
                className: generalStyles.tableCell,
                Cell: ({ row }) => row.original.restrictionValue,
            },
        ],
        [tableData, roles]
    );

    return (
        <>
            <h1>Blacklist Domains</h1>
            <hr />
            {roles?.includes(CSR_ROLES.superUser) && (
                <div className={styles.buttonWrapper}>
                    <button className={generalStyles.smallButton} onClick={() => setOpenModal(true)}>
                        Add
                    </button>
                </div>
            )}
            <div className={styles.tableWrapper}>
                <DataTable
                    data={tableData}
                    columns={columnDef}
                    sorting={[
                        {
                            id: 'restrictionValue',
                            desc: false,
                        },
                    ]}
                />
            </div>
            <Popup
                open={openModal}
                onClose={() => {
                    setOpenModal(false);
                }}
                modal
                closeOnDocumentClick={false}
                contentStyle={{ width: '400px' }}
            >
                {() => (
                    <div className={classNames(generalStyles.popup, styles.popup)}>
                        <h4 className={styles.popupTitle}>Add Domain to Blacklist</h4>
                        <ByzzerTextInput
                            className={classNames(generalStyles.input, styles.popupInput)}
                            placeholder="Domain"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.value)}
                        />
                        <div className={styles.popupButtonWrapper}>
                            {(apiStates.addDomain.error || apiStates.addDomain.successMessage) && (
                                <span
                                    className={classNames(styles.message, {
                                        [generalStyles.errorMessage]: apiStates.addDomain.state === API_STATES.error,
                                        [generalStyles.successMessage]:
                                            apiStates.addDomain.state === API_STATES.success,
                                    })}
                                >
                                    {apiStates.addDomain.error || apiStates.addDomain.successMessage}
                                </span>
                            )}
                            <button
                                className={classNames(generalStyles.smallButton, styles.secondaryButton)}
                                onClick={() => {
                                    setInputValue('');
                                    setOpenModal(false);
                                }}
                                disabled={apiStates.addDomain.state === API_STATES.loading}
                            >
                                Close
                            </button>
                            <button
                                className={classNames(generalStyles.smallButton)}
                                onClick={onSubmit}
                                disabled={apiStates.addDomain.state === API_STATES.loading}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                )}
            </Popup>
        </>
    );
};

export default BlacklistEmail;
